import {gql} from '@apollo/client';
import {createStyles, Group} from '@mantine/core';
import React, {useCallback, useMemo} from 'react';
import _ from 'underscore';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import HomeDashboardActions from 'home-dashboard/HomeDashboardActions';
import HomeDashboardStore from 'home-dashboard/HomeDashboardStore';
import useOnMount from 'ui-library/hooks/useOnMount';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import Spinner from 'ui-library/Spinner';

import {HomepageAssignedProcessTasksQuery} from './__generated/HomepageAssignedProcessTasksQuery';
import CardSection, {CardSectionLoadingRows} from './CardSection';
import {HOMEPAGE_SECTION_TEST_COMPONENT, LOADING_SKELETON_ROW_COUNT} from './HomepageConstants';
import {
  getEnabledMyWorkCategories,
  hasReviewsLicense,
  shouldShowAssignedProcessTasks,
} from './HomepageVisibility';
import MyWorkSectionContent from './my-work/MyWorkSectionContent';

const TEST_COMPONENT = HOMEPAGE_SECTION_TEST_COMPONENT.MY_WORK;

const useMyWorkSectionLoadingStyles = createStyles((theme) => ({
  filterRow: {
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    padding: theme.spacing.md,
  },
  categoryLoadingSpinner: {
    '&&': {
      width: '16px',
      height: '16px',
    },
  },
}));

export const HOMEPAGE_ASSIGNED_PROCESS_TASKS_QUERY = gql`
  query HomepageAssignedProcessTasksQuery @skipAudit @engComponent(name: NAVIGATION) {
    assignedProcessTaskStatusSummaries(
      processTaskStatusTypes: [PENDING, IN_PROGRESS, COMPLETED, FAILED, INVALID, SKIPPED, CANCELLED]
    ) {
      ... on ProcessTaskStatusSummaryItemNode {
        count
        taskIds
        status {
          ... on ProcessTaskStatusNode {
            id
            displayName
            statusType
          }
        }
      }
    }
  }
`;

/** Entry, worksheet, and process-task work queues with per-category status filters. */
export default function MyWorkSectionLegacy(): JSX.Element | null {
  const showAssignedProcessTasks = shouldShowAssignedProcessTasks();

  useOnMount(() => {
    // Browsers cap the number of simultaneous network requests to a given domain - typically around 6.
    // <Sidepanel> (which renders this component) does a few expensive GraphQL queries, and since Apollo
    // always defers queries for a tick, if we don't match that behavior here, these network requests are
    // fired first, and ultimately make page load slower (we want the slow network requests to start as soon
    // as possible).
    _.defer(() => {
      if (hasReviewsLicense()) {
        HomeDashboardActions.fetchEntryReviewSummary();
        HomeDashboardActions.fetchWorksheetReviewSummary();
      }
    });
  });

  const entryReviewSummary = useStoreSelector(
    HomeDashboardStore,
    useCallback((store) => store.getEntryReviewSummary(), [])
  );
  const worksheetReviewSummary = useStoreSelector(
    HomeDashboardStore,
    useCallback((store) => store.getWorksheetReviewSummary(), [])
  );

  const enabledCategories = useMemo(
    () => getEnabledMyWorkCategories({entryReviewSummary, worksheetReviewSummary}),
    [entryReviewSummary, worksheetReviewSummary]
  );

  const tasksQueryResult = useSkippableBenchlingQuery<HomepageAssignedProcessTasksQuery>(
    HOMEPAGE_ASSIGNED_PROCESS_TASKS_QUERY,
    {skip: !showAssignedProcessTasks}
  );

  const isWaitingOnReviewSummaries =
    hasReviewsLicense() && (entryReviewSummary == null || worksheetReviewSummary == null);
  const isWaitingOnTasksSummary =
    showAssignedProcessTasks && tasksQueryResult.type === QueryResultType.PENDING;
  const isCategoryDataLoading = isWaitingOnReviewSummaries || isWaitingOnTasksSummary;
  const {classes: loadingClasses} = useMyWorkSectionLoadingStyles();

  if (isCategoryDataLoading) {
    return (
      <CardSection title="My work" dataTestAttrs={{component: TEST_COMPONENT, element: 'root'}}>
        <Group spacing="xs" className={loadingClasses.filterRow}>
          <Spinner classNames={loadingClasses.categoryLoadingSpinner} light={true} />
        </Group>
        <div>
          <CardSectionLoadingRows count={LOADING_SKELETON_ROW_COUNT} />
        </div>
      </CardSection>
    );
  }

  if (enabledCategories.length === 0) {
    return null;
  }

  return (
    <MyWorkSectionContent
      entryReviewSummary={entryReviewSummary}
      worksheetReviewSummary={worksheetReviewSummary}
      tasksQueryResult={tasksQueryResult}
      enabledCategories={enabledCategories}
    />
  );
}
